import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useScrollReveal, useCounter } from '../../hooks/useScrollAnimations';

const ProblemSection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  // Parallax effects for different elements
  const yTitle = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const yText = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1, 0.8]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  const { ref: statsRef, isInView: statsInView } = useScrollReveal(0.3);
  const { ref: counter1Ref, count: hungerRate } = useCounter(31);
  const { ref: counter2Ref, count: missingMeals } = useCounter(40);
  const { ref: counter3Ref, count: employed } = useCounter(75);
  const { ref: videosRef, isInView: videosInView } = useScrollReveal(0.5);

  // Track which card is in view to change the image
  const [activeImage, setActiveImage] = useState(0);

  const images = [
    '/content/tmf data/images/Volunteer Brookline Food Pantry.jpg',
    '/content/tmf data/images/Food Pantry Volunteer Photo 2017.jpg',
    '/content/tmf data/images/Food Community Services.jpg'
  ];

  // Intersection Observer to detect which section is in view
  useEffect(() => {
    const sections = document.querySelectorAll('.tmf-problem-snap-section');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionIndex = parseInt(entry.target.getAttribute('data-section') || '0');
            // Section 0 is empty, sections 1-3 correspond to images 0-2
            if (sectionIndex > 0) {
              setTimeout(() => {
                setActiveImage(sectionIndex - 1);
              }, 200);
            }
          }
        });
      },
      {
        threshold: 0.5,
        rootMargin: '0px'
      }
    );

    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  return (
    <section className="tmf-problem-section" ref={containerRef}>
      <div className="tmf-container">
        {/* Cinematic Title with Parallax */}
        <motion.div
          className="tmf-problem-cinematic-title"
          style={{ y: yTitle, scale, opacity }}
        >
          <motion.h2
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            The Beginning
          </motion.h2>
          <motion.div
            className="tmf-problem-subtitle"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <p style={{ color: 'var(--tmf-subtitle-color)' }}>
              When we first started this project, I didn't fully understand the scale of what we were getting into. The challenges we'll talk about often remain invisible to most people, hidden in plain sight. But there are incredible individuals dedicating their lives to bridging these gaps, and their work deserves recognition.
            </p>
          </motion.div>
        </motion.div>
      </div>

      {/* Split-Screen Scroll Section - Outside container for full width and proper sticky behavior */}
      <div className="tmf-problem-scroll-container">
          {/* Fixed Image on Left - Desktop Only */}
          <div className="tmf-problem-fixed-image">
            <div className="tmf-problem-image-wrapper">
              {images.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt="Food pantry context"
                  className="tmf-problem-image"
                  style={{
                    filter: 'grayscale(100%)',
                    opacity: activeImage === index ? 1 : 0,
                    transition: 'opacity 0.8s ease-in-out'
                  }}
                />
              ))}
            </div>
          </div>

          {/* Scrollable Content on Right */}
          <div className="tmf-problem-scroll-sections">
            {/* Section 1: Empty (Image Reveal) - Desktop Only */}
            <div className="tmf-problem-snap-section tmf-problem-snap-empty" data-section="0">
              {/* Empty - just for image reveal */}
            </div>

            {/* Section 2: Card 01 */}
            <div className="tmf-problem-snap-section" data-section="1">
              <div className="tmf-problem-card">
                <span className="tmf-problem-split-number">01</span>
                <h3>The Scale of Need</h3>
                <p>
                  In Marion County, Indiana, 31% of residents experience food insecurity. That's nearly 1 in 3 people lacking consistent access to nutritious food. Among those struggling, 40% are actively missing meals. But here's what surprised us: more than 75% of these households have someone employed. <span style={{ color: '#ff6b6b' }}>This isn't an unemployment crisis. Something else is happening.</span>
                </p>
              </div>
            </div>

            {/* Section 3: Card 02 */}
            <div className="tmf-problem-snap-section" data-section="2">
              <div className="tmf-problem-card">
                <span className="tmf-problem-split-number">02</span>
                <h3>The Barriers</h3>
                <p>
                  Food insecurity affects communities disproportionately. Language barriers, transportation challenges, and systemic inequities create layers of difficulty. Federal nutrition programs like SNAP provide 63% of the food safety net. <span style={{ color: '#ff6b6b' }}>But these systems weren't designed for the people who need them most.</span> Even when help exists, reaching it becomes another challenge entirely.
                </p>
              </div>
            </div>

            {/* Section 4: Card 03 */}
            <div className="tmf-problem-snap-section" data-section="3">
              <div className="tmf-problem-card">
                <span className="tmf-problem-split-number">03</span>
                <h3>The Deeper Question</h3>
                <p>
                  Food pantries serve as critical access points for vulnerable communities. Many offer comprehensive services like career counseling, financial assistance, and family support programs. <span style={{ color: '#ff6b6b' }}>But if people don't know these services exist, or can't access them, the full potential of these organizations remains untapped.</span> The question isn't just about addressing hunger. It's about connecting people to the resources that can break the cycle.
                </p>
              </div>
            </div>

            {/* Section 5: Empty (Release Sticky) */}
            <div className="tmf-problem-snap-section tmf-problem-snap-empty" data-section="4">
              {/* Empty - releases the sticky image */}
            </div>
          </div>
        </div>

      {/* Floating Stats Cards with Stagger */}
      <div className="tmf-container">
        <motion.div
          ref={statsRef}
          className="tmf-problem-stats-floating"
          style={{ y: yText }}
        >
          <motion.div
            className="tmf-problem-stat-float"
            ref={counter1Ref}
            initial={{ opacity: 0, y: 100, rotateX: -15 }}
            whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <motion.div
              className="tmf-problem-stat-glow"
              animate={{
                opacity: [0.3, 0.6, 0.3],
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <div className="tmf-problem-stat-number">{hungerRate}%</div>
            <div className="tmf-problem-stat-label">Face Access Barriers</div>
            <p className="tmf-problem-stat-description">
              Of Marion County residents face significant barriers in accessing food resources, including transportation challenges, language differences, and systemic inequities.
            </p>
          </motion.div>

          <motion.div
            className="tmf-problem-stat-float"
            ref={counter2Ref}
            initial={{ opacity: 0, y: 100, rotateX: -15 }}
            whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <motion.div
              className="tmf-problem-stat-glow"
              animate={{
                opacity: [0.3, 0.6, 0.3],
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1
              }}
            />
            <div className="tmf-problem-stat-number">{missingMeals}%</div>
            <div className="tmf-problem-stat-label">Unaware of Services</div>
            <p className="tmf-problem-stat-description">
              Come only for food assistance, unaware that pantries offer case management, benefits enrollment, and comprehensive support services.
            </p>
          </motion.div>

          <motion.div
            className="tmf-problem-stat-float"
            ref={counter3Ref}
            initial={{ opacity: 0, y: 100, rotateX: -15 }}
            whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <motion.div
              className="tmf-problem-stat-glow"
              animate={{
                opacity: [0.3, 0.6, 0.3],
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 2
              }}
            />
            <div className="tmf-problem-stat-number">{employed}%</div>
            <div className="tmf-problem-stat-label">Have Jobs</div>
            <p className="tmf-problem-stat-description">
              Are employed but still need food assistance. This is a mobility and access issue, not an employment problem.
            </p>
          </motion.div>
        </motion.div>

        {/* Citation Source */}
        <motion.div
          className="tmf-problem-citation"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          viewport={{ once: true, amount: 0.8 }}
        >
          <p className="tmf-problem-citation-text">
            Sources: Feeding America (2024), Marion County Food Insecurity Data; Indiana University Public Policy Institute; Southeast Community Services Annual Report (2023)
          </p>
        </motion.div>

        {/* Finale - Connecting to Toyota & Our Work */}
        <motion.div
          className="tmf-problem-finale"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className="tmf-problem-finale-content">
            <h3>Where Technology Meets Real Need</h3>

            {/* Frosted Card for First Paragraph */}
            <motion.div
              className="tmf-problem-finale-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              <div className="tmf-problem-finale-logo">
                <img
                  src="/content/tmf data/tmf logos/Pantry Southeast Community Services.avif"
                  alt="Southeast Community Services"
                />
              </div>
              <p>
                Southeast Community Services is one of those pantries working tirelessly to serve their community. They're our partner in this research, alongside the Indy Hunger Network and the Toyota Mobility Foundation. The challenge is clear: people come for food, but most don't know about the comprehensive services available to them. Career counseling, benefits enrollment, financial assistance, all underutilized because of <span className="tmf-dramatic-text">access and awareness barriers</span>.
              </p>
            </motion.div>

            {/* Cinematic Video Row */}
            <motion.div
              ref={videosRef}
              className="tmf-problem-finale-videos"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              <div className="tmf-problem-video-wrapper">
                <iframe
                  src="https://www.youtube.com/embed/EedMmMedj3M?autoplay=1&mute=1&loop=1&playlist=EedMmMedj3M&start=47&controls=0&modestbranding=1"
                  title="Toyota Mobility Foundation Context Video 1"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="tmf-problem-video"
                ></iframe>
              </div>
              <div className="tmf-problem-video-wrapper">
                <iframe
                  src="https://www.youtube.com/embed/Zpz5FZjuPGE?autoplay=1&mute=1&loop=1&playlist=Zpz5FZjuPGE&start=93&controls=0&modestbranding=1"
                  title="Toyota Mobility Foundation Context Video 2"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="tmf-problem-video"
                ></iframe>
              </div>
            </motion.div>

            <p>
              That's exactly where Toyota comes in. They have the technology. Autonomous vehicles, supply chain logistics, mobility solutions. And this isn't theoretical. <span className="tmf-dramatic-text">They've done it before</span>. During COVID, Toyota developed contactless autonomous delivery systems for vulnerable communities, proving that mobility technology can serve social needs. But having the technology is only part of the equation. They want to understand <span className="tmf-dramatic-text">where and how to apply it</span> to make a real, lasting difference. Our research helps them understand the context, the barriers, the human needs. By deeply understanding how people access these services (or fail to), Toyota can deploy their resources with precision and adaptability.
            </p>

            {/* Frosted Card for Research Question */}
            <motion.div
              className="tmf-problem-finale-card tmf-research-question-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              <p>
                This is what we call{' '}
                <motion.span
                  className={`tmf-highlight-text ${videosInView ? 'tmf-gradient-reveal' : ''}`}
                  initial={{ opacity: 0 }}
                  animate={videosInView ? { opacity: 1 } : { opacity: 0 }}
                  transition={{ duration: 0.5, delay: 2, ease: "easeOut" }}
                >
                  Community-Rooted Planning for Mobility as a Social Service
                </motion.span>
                <motion.span
                  className={videosInView ? 'tmf-gradient-reveal-secondary' : ''}
                  initial={{ opacity: 0 }}
                  animate={videosInView ? { opacity: 1 } : { opacity: 0 }}
                  transition={{ duration: 0.5, delay: 3.5, ease: "easeOut" }}
                >
                  {' '}starting with people's lived experiences to inform where and how mobility solutions can truly serve communities.
                </motion.span>
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSection;
